// ====== TAX CALCULATOR ======
// Pure functions tính thuế — không phụ thuộc React
// Tái sử dụng trong toàn bộ phần mềm

import {
  TAX_BRACKETS_2026, TAX_BRACKETS_2025,
  DEDUCTION_2026, DEDUCTION_2025,
  INSURANCE_RATE, BIZ_TAX_EXEMPT, BIZ_TAX_QUARTER_EXEMPT, BIZ_INDUSTRIES,
} from '../constants/taxConstants';

// ====== FORMATTING UTILITIES ======

/** Format số thành VNĐ: 1.000.000 */
export const fmt = (n) => {
  if (n === undefined || n === null || isNaN(n)) return "0";
  return Math.round(n).toLocaleString("vi-VN");
};

/** Format rút gọn: 1.5 tr, 2.1 tỷ */
export const fmtM = (n) => {
  if (Math.abs(n) >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + " tỷ";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + " tr";
  return fmt(n);
};

// ====== CORE TAX ENGINE ======

/**
 * Tính thuế lũy tiến từng phần
 * @param {number} taxableIncome Thu nhập tính thuế
 * @param {Array} brackets Biểu thuế (mặc định: 2026)
 * @returns {{ total: number, breakdown: Array }}
 */
export const calcTax = (taxableIncome, brackets = TAX_BRACKETS_2026) => {
  if (taxableIncome <= 0) return { total: 0, breakdown: [] };
  const breakdown = [];
  let remaining = taxableIncome;
  let total = 0;
  for (const b of brackets) {
    const range = b.max === Infinity ? Infinity : b.max - b.min;
    const taxable = Math.min(remaining, range);
    if (taxable <= 0) break;
    const tax = taxable * b.rate;
    breakdown.push({ ...b, taxable, tax });
    total += tax;
    remaining -= taxable;
  }
  return { total, breakdown };
};

// ====== SALARY TAX (NLĐ) ======

/**
 * Tính đầy đủ thuế TNCN cho NLĐ
 * @param {Object} params
 * @param {number} params.grossIncome Lương Gross
 * @param {number} params.dependents Số người phụ thuộc
 * @param {number} params.otherDeductions Giảm trừ khác
 * @param {number} params.insuranceSalary Lương đóng BH (nếu khác Gross)
 * @param {boolean} params.useCustomInsurance Có dùng lương BH riêng không
 * @param {number} params.allowanceMeal Phụ cấp ăn trưa
 * @param {number} params.allowanceOther Phụ cấp khác miễn thuế
 * @param {number} params.bonus13 Thưởng tháng 13
 * @param {string} params.contractType "long" | "short"
 * @returns {Object} Kết quả tính thuế đầy đủ
 */
export function calcSalaryTax({
  grossIncome = 0,
  dependents = 0,
  otherDeductions = 0,
  insuranceSalary = 0,
  useCustomInsurance = false,
  allowanceMeal = 0,
  allowanceOther = 0,
  bonus13 = 0,
  contractType = "long",
} = {}) {
  const insBase = useCustomInsurance ? insuranceSalary : grossIncome;
  const cappedInsBase = Math.min(insBase, 46_800_000);
  const insurance = Math.round(cappedInsBase * INSURANCE_RATE);
  const mealExempt = Math.min(allowanceMeal, 730_000);
  const totalExempt = mealExempt + allowanceOther;
  const incomeBeforeTax = grossIncome - totalExempt;
  const totalDeductions = insurance + DEDUCTION_2026.self + (dependents * DEDUCTION_2026.dependent) + otherDeductions;
  const taxableIncome2026 = Math.max(0, incomeBeforeTax - totalDeductions);
  const taxableIncome2025 = Math.max(0, incomeBeforeTax - insurance - DEDUCTION_2025.self - (dependents * DEDUCTION_2025.dependent) - otherDeductions);
  const result2026 = calcTax(taxableIncome2026, TAX_BRACKETS_2026);
  const result2025 = calcTax(taxableIncome2025, TAX_BRACKETS_2025);
  const netIncome = grossIncome - insurance - result2026.total;
  const effectiveRate = grossIncome > 0 ? ((result2026.total / grossIncome) * 100).toFixed(1) : "0.0";
  const taxThreshold = DEDUCTION_2026.self + (dependents * DEDUCTION_2026.dependent);
  const minGrossForTax = Math.round((taxThreshold) / (1 - INSURANCE_RATE));
  const shortContractTax = contractType === "short" ? Math.round(grossIncome * 0.10) : 0;

  return {
    insurance,
    mealExempt,
    totalExempt,
    incomeBeforeTax,
    totalDeductions,
    taxableIncome2026,
    taxableIncome2025,
    result2026,
    result2025,
    netIncome,
    effectiveRate,
    taxThreshold,
    minGrossForTax,
    shortContractTax,
    bonus13Taxable: Math.max(0, bonus13),
    monthlyAvgWithBonus: grossIncome + Math.round(bonus13 / 12),
  };
}

/**
 * Tính Gross từ Net (binary search)
 * @param {Object} params
 * @param {number} params.targetNet Lương thực nhận mong muốn
 * @param {number} params.dependents Số người phụ thuộc
 * @param {number} params.otherDeductions Giảm trừ khác
 * @param {number} params.insuranceSalary Lương đóng BH
 * @param {boolean} params.useCustomInsurance Có dùng lương BH riêng không
 * @param {number} params.allowanceMeal Phụ cấp ăn trưa
 * @param {number} params.allowanceOther Phụ cấp khác miễn thuế
 * @returns {number} Lương Gross tương ứng
 */
export function calcGrossFromNet({
  targetNet = 0,
  dependents = 0,
  otherDeductions = 0,
  insuranceSalary = 0,
  useCustomInsurance = false,
  allowanceMeal = 0,
  allowanceOther = 0,
} = {}) {
  if (targetNet <= 0) return 0;
  let lo = targetNet, hi = targetNet * 2;
  for (let i = 0; i < 50; i++) {
    const mid = Math.round((lo + hi) / 2);
    const insB = useCustomInsurance ? insuranceSalary : mid;
    const cappedB = Math.min(insB, 46_800_000);
    const ins = Math.round(cappedB * INSURANCE_RATE);
    const mealEx = Math.min(allowanceMeal, 730_000);
    const totEx = mealEx + allowanceOther;
    const incBT = mid - totEx;
    const totDed = ins + DEDUCTION_2026.self + (dependents * DEDUCTION_2026.dependent) + otherDeductions;
    const taxableInc = Math.max(0, incBT - totDed);
    const taxResult = calcTax(taxableInc, TAX_BRACKETS_2026);
    const computedNet = mid - ins - taxResult.total;
    if (Math.abs(computedNet - targetNet) <= 1) return mid;
    if (computedNet < targetNet) lo = mid + 1;
    else hi = mid - 1;
  }
  return Math.round((lo + hi) / 2);
}

// ====== BUSINESS TAX (HKD) ======

/**
 * Tính thuế GTGT & TNCN cho Hộ kinh doanh
 * @param {Object} params
 * @param {number} params.revenue Doanh thu năm
 * @param {string} params.industryId ID ngành nghề (trade, service_goods, service, manufacturing, other)
 * @param {string} params.method "revenue" | "profit"
 * @param {number} params.cost Chi phí đầu vào (chỉ dùng khi method="profit")
 * @returns {Object} Kết quả tính thuế HKD
 */
export function calcBusinessTax({ revenue = 0, industryId = "trade", method = "revenue", cost = 0 } = {}) {
  const selectedIndustry = BIZ_INDUSTRIES.find(i => i.id === industryId) || BIZ_INDUSTRIES[0];
  const isExempt = revenue <= BIZ_TAX_EXEMPT;
  const revenueGroup = revenue <= BIZ_TAX_EXEMPT ? 1 : revenue <= 3_000_000_000 ? 2 : revenue <= 50_000_000_000 ? 3 : 4;
  const effectiveMethod = revenueGroup >= 3 ? "profit" : method;
  const vat = isExempt ? 0 : Math.round(revenue * selectedIndustry.vatRate);

  let pit = 0, pitLabel = "";
  if (isExempt) {
    pitLabel = "Miễn thuế (DT ≤ 1 tỷ)";
  } else if (effectiveMethod === "revenue") {
    const taxableRevenue = Math.max(0, revenue - BIZ_TAX_EXEMPT);
    pit = Math.round(taxableRevenue * selectedIndustry.pitRate);
    pitLabel = `${(selectedIndustry.pitRate * 100)}% × (DT − 1 tỷ)`;
  } else {
    const profit = Math.max(0, revenue - cost);
    const pitTaxRate = revenue > 50_000_000_000 ? 0.20 : revenue > 3_000_000_000 ? 0.17 : 0.15;
    pit = Math.round(profit * pitTaxRate);
    pitLabel = `${(pitTaxRate * 100)}% × Lợi nhuận`;
  }

  return {
    selectedIndustry,
    isExempt,
    revenueGroup,
    effectiveMethod,
    vat,
    pit,
    pitLabel,
    totalTax: vat + pit,
  };
}

// ====== ENTERPRISE TAX (DN) ======

/**
 * Tính thuế TNDN cho Doanh nghiệp
 * @param {Object} params
 * @param {number} params.revenue Doanh thu
 * @param {number} params.cost Chi phí
 * @param {number} params.otherIncome Thu nhập khác
 * @param {number} params.exemptIncome Thu nhập miễn thuế
 * @param {number} params.lossCarry Lỗ kết chuyển
 * @param {string} params.vatMethod "deduct" | "direct"
 * @param {number} params.vatOut VAT đầu ra
 * @param {number} params.vatIn VAT đầu vào
 * @returns {Object} Kết quả tính thuế DN
 */
export function calcEnterpriseTax({
  revenue = 0, cost = 0, otherIncome = 0,
  exemptIncome = 0, lossCarry = 0,
  vatMethod = "deduct", vatOut = 0, vatIn = 0,
} = {}) {
  const taxableIncome = Math.max(0, revenue - cost + otherIncome - exemptIncome - lossCarry);
  const taxRate = revenue <= 3_000_000_000 ? 0.15 : revenue <= 50_000_000_000 ? 0.17 : 0.20;
  const tndn = Math.round(taxableIncome * taxRate);
  const vat = vatMethod === "deduct" ? Math.max(0, vatOut - vatIn) : Math.round(revenue * 0.01);
  const profit = revenue - cost + otherIncome - tndn;

  return {
    taxableIncome,
    taxRate,
    tndn,
    vat,
    profit,
  };
}

// ====== TỜ KHAI COMPUTATION ======

/**
 * Tính dữ liệu cho Tờ khai 01/CNKD
 * @param {Object} params
 * @param {string} params.industryId ID ngành nghề
 * @param {Array<number>} params.monthlyRevenue Doanh thu 3 tháng [m1, m2, m3]
 * @param {number} params.exemptAmount Ngưỡng miễn thuế TNCN quý
 * @returns {Object} Dữ liệu tờ khai
 */
export function calcTkData({ industryId = "trade", monthlyRevenue = [0, 0, 0], exemptAmount = BIZ_TAX_QUARTER_EXEMPT } = {}) {
  const industry = BIZ_INDUSTRIES.find(i => i.id === industryId) || BIZ_INDUSTRIES[0];
  const totalRevenue = monthlyRevenue.reduce((a, b) => a + b, 0);
  const vat = Math.round(totalRevenue * industry.vatRate);
  const taxableRevenue = Math.max(0, totalRevenue - exemptAmount);
  const pit = Math.round(taxableRevenue * industry.pitRate);
  const vatPerMonth = monthlyRevenue.map(d => Math.round(d * industry.vatRate));
  const pitTotal = pit;
  const pitPerMonth = Math.round(pitTotal / 3);
  const pitMonthly = monthlyRevenue.map((d, i) => i < 2 ? pitPerMonth : pitTotal - pitPerMonth * 2);

  return {
    industry,
    totalRevenue,
    vat,
    taxableRevenue,
    pit,
    vatPerMonth,
    pitMonthly,
    pitTotal,
    exemptAmount,
  };
}

// ====== SỔ SÁCH HELPERS ======

/**
 * Tính tổng hợp cho entries sổ sách
 * @param {Array} entries Danh sách entries
 * @param {string} defaultIndustryId ID ngành mặc định
 * @returns {Object} Tổng hợp
 */
export function calcSoSummary(entries, defaultIndustryId = "trade") {
  const soTotal = entries.reduce((s, e) => s + (e.amount || 0), 0);
  const soCostTotal = entries.reduce((s, e) => s + (e.cost || 0), 0);
  const soVatTotal = entries.reduce((s, e) => s + (e.vatAmount || 0), 0);
  const soPitTotal = entries.reduce((s, e) => s + (e.pitAmount || 0), 0);

  const byIndustry = BIZ_INDUSTRIES.map(ind => {
    const items = entries.filter(e => (e.industry || defaultIndustryId) === ind.id);
    if (items.length === 0) return null;
    return {
      ...ind,
      count: items.length,
      total: items.reduce((s, e) => s + (e.amount || 0), 0),
      vat: items.reduce((s, e) => s + (e.vatAmount || 0), 0),
      pit: items.reduce((s, e) => s + (e.pitAmount || 0), 0),
    };
  }).filter(Boolean);

  return { soTotal, soCostTotal, soVatTotal, soPitTotal, byIndustry };
}

/**
 * Auto-calc tax cho entry sổ sách (S2a/S2b)
 * @param {Object} entry Entry hiện tại
 * @param {string} soType Loại sổ (s2a, s2b)
 * @param {string} defaultIndustryId ID ngành mặc định
 * @returns {Object} Entry đã tính thuế
 */
export function calcEntryTax(entry, soType, defaultIndustryId = "trade") {
  const ind = BIZ_INDUSTRIES.find(x => x.id === (entry.industry || defaultIndustryId));
  const amt = entry.amount || 0;
  const updated = { ...entry };
  updated.vatAmount = Math.round(amt * (ind?.vatRate || 0.01));
  if (soType === "s2a") updated.pitAmount = Math.round(amt * (ind?.pitRate || 0.005));
  return updated;
}
